import React, { useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { isEqual, get, includes } from 'lodash';
import { bindActionCreators } from 'redux';

import resourcesConfig from '../../resources';
import asyncRoute from '../../helpers/hoc/asyncRoute';
import ThemeContext from '../../containers/ThemeContext';
import { isNativeWebView } from '../../utils/helpers';
import { nativeEventType } from '../../config';

import './LocationPickerAnimation.scss';

const LocationPermissionDenialCard = asyncRoute(() =>
  import('../Card/LocationPermissionDenialCard'),
);
const Icons = asyncRoute(() => import('../Icons'));

const LocationPickerAnimation = ({
  setShowLocationAnimation,
  setLocationPermissionPromptDismissed,
  guestLocationDetails,
}) => {
  const { fetchingLocation, guestLocation, locationPermissionStatus, setShowDeliveryAddressPopup } =
    useContext(ThemeContext);
  const { addressName, address } = guestLocationDetails || {};

  const [animationFinished, setAnimationFinished] = useState(false);
  const [manualSelectionActive, setManualSelectionActive] = useState(false);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    let animationTimer;

    if (!mountedRef.current) return;

    if (isEqual(locationPermissionStatus, 'granted') && !animationFinished) {
      // Simulate animation finish after location is fetched
      if (!fetchingLocation && guestLocation) {
        animationTimer = setTimeout(() => {
          if (mountedRef.current) {
            // To Do: Add animation finish logic
            setAnimationFinished(true);
          }
        }, 1000);
      }
    }

    return () => clearTimeout(animationTimer);
  }, [locationPermissionStatus, fetchingLocation, guestLocation, animationFinished]);

  useEffect(() => {
    let dismissalTimer;

    if (!fetchingLocation && animationFinished) {
      dismissalTimer = setTimeout(() => {
        // Remove Location Screen After Displaying Location Details for 1.5 seconds
        setShowLocationAnimation(false);
      }, 1500);
    }

    return () => clearTimeout(dismissalTimer);
  }, [fetchingLocation, animationFinished]);

  const handleChangeLocationCTA = () => {
    if (isNativeWebView) {
      const data = {
        eventType: {
          type: nativeEventType.openLocationSettings,
        },
      };
      window.ReactNativeWebView.postMessage(JSON.stringify(data));
    }
  };

  const handleManualLocationCTA = () => {
    setManualSelectionActive(true);
    setLocationPermissionPromptDismissed(true);
    setShowLocationAnimation(false);
    setShowDeliveryAddressPopup(true);
  };

  const locationAccessUnavailable = includes(
    ['denied', 'services-disabled', 'unavailable', 'undetermined'],
    locationPermissionStatus,
  );

  if (manualSelectionActive) return null;

  return (
    <div className="location-picker-animation">
      <div className="location-picker-animation_content" role="dialog" aria-modal="true">
        {!locationAccessUnavailable && (fetchingLocation || !animationFinished) ? (
          <div className="location-picker-animation_status-card">
            <div className="location-picker-animation_image">
              <Icons type="location" />
            </div>
            <div className="location-picker-animation_title">
              <FormattedMessage
                id="identifyingLocation"
                defaultMessage={resourcesConfig.identifyingLocation}
              />
            </div>
            <div className="location-picker-animation_description">
              <FormattedMessage
                id="identifyingLocationDescription"
                defaultMessage={resourcesConfig.identifyingLocationDescription}
              />
            </div>
          </div>
        ) : locationAccessUnavailable ? (
          <LocationPermissionDenialCard
            modalStyle
            handleCTAClick={isNativeWebView ? handleChangeLocationCTA : handleManualLocationCTA}
            handleSecondaryCTAClick={isNativeWebView ? handleManualLocationCTA : null}
            buttonCTA={
              isNativeWebView ? (
                <FormattedMessage
                  id="allowLocationAccess"
                  defaultMessage={resourcesConfig.allowLocationAccess}
                />
              ) : (
                <FormattedMessage
                  id="selectLocationManually"
                  defaultMessage={resourcesConfig.selectLocationManually}
                />
              )
            }
            secondaryButtonCTA={
              isNativeWebView ? (
                <FormattedMessage
                  id="selectLocationManually"
                  defaultMessage={resourcesConfig.selectLocationManually}
                />
              ) : null
            }
          />
        ) : (
          <div className="location-picker-animation_status-card">
            <div className="location-picker-animation_image">
              <Icons type="location" />
            </div>

            {addressName && <div className="location-picker-animation_title">{addressName}</div>}
            {address && (
              <div className="location-picker-animation_locality-container">
                <div className="location-picker-animation_locality-text">{address}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  serviceTypeConfig: get(state, 'common.resources.serviceTypeConfig', {}),
  brandResources: get(state, 'common.resources', {}),
  guestLocationDetails: get(state, 'common.guestLocation', {}),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({}, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(LocationPickerAnimation);
